import { z } from "zod";

// The Admin section: which tabs exist, what they are called, which one a bare
// `/admin` opens, and who may enter at all.
//
// It lives in `lib/` for the reason that tier exists — it is named from more
// than one place and depends on nothing above it: the route's search contract
// (`@/api/contracts/admin-tabs`) validates against it and the shell
// (`@/components/admin/AdminPage`) paints it. See `client/docs/architecture.md`,
// "The dependency rule", and the same split `@/lib/auth-error-codes` makes for
// the `?error=` vocabulary.
//
// The slugs are the *URL* surface: the old stack's Admin page carried its
// active tab in a `?tab=<slug>` param, and the shell keeps that shape so
// cutover redirects can map old admin URLs onto Atlas (#222). Renaming a slug
// is therefore a cutover-redirect change, not a refactor.

/**
 * The tabs the shell renders, in bar order.
 *
 * The remaining donor tabs arrive with their own domains (#222). Adding one is
 * three compiler-checked edits — a slug here, a label below, and a panel in
 * `AdminPage.tsx` — because both of those are exhaustive `Record`s over this
 * tuple. That is the whole "adding a tab must be cheap" requirement, enforced
 * rather than documented.
 *
 * **Core Spec Links is first, and so is what a bare `/admin` opens**
 * (`DEFAULT_ADMIN_TAB_SLUG` below) — the source's bar order and default
 * (`admin-page.tsx:24-111`, index 0), ruled on #981 Q15 (#1085). System Health
 * held the front until then, and the move is a ruling rather than an append
 * because it changes where every existing bare `/admin` link lands. The slug is
 * the source's own, `core-spec-links`, a cutover-redirect surface like
 * Integrations' below.
 *
 * The catalog's four admin surfaces — manufacturers, materials, finishes,
 * tiers — are **one** tab, not four and not the two they started as. An admin
 * works one domain at a time, so the tab bar carries the section's *areas* and
 * the Item Catalog tab carries its own sub-tabs (`CATALOG_PANES` in
 * `@/lib/catalog-admin`, reached as `?catalog=<pane>`). Splitting them across
 * the top bar instead would put four peers up there beside System Health and
 * imply they are as far apart from each other as they are from it.
 *
 * Integrations keeps **the source's slug, `external-apis`** (`admin-page.tsx:
 * 76-84`), not one derived from its label: it is a cutover-redirect surface,
 * and old admin links carry it. It went in after Item Catalog so the bar's
 * order at the time was unchanged (#747).
 *
 * Share Links (#1023; ADR-0019 decision 10) is Atlas's own tab, with no source
 * slug to keep, and goes last for the same reason Integrations went after Item
 * Catalog: appending moves no existing tab.
 */
export const ADMIN_TAB_SLUGS = [
  "core-spec-links",
  "system-health",
  "item-catalog",
  "external-apis",
  "share-links",
] as const;

export const adminTabSlugSchema = z.enum(ADMIN_TAB_SLUGS);

export type AdminTabSlug = z.infer<typeof adminTabSlugSchema>;

/**
 * The bar label for each tab.
 *
 * "System Health" rather than the old stack's "Data Health": that name is
 * ETL-specific and stays available for a future data-migration surface (#222).
 */
export const ADMIN_TAB_LABELS: Record<AdminTabSlug, string> = {
  // The source's label verbatim (`admin-page.tsx`); the bar uppercases it.
  "core-spec-links": "Core Spec Links",
  "system-health": "System Health",
  // "Item Catalog" rather than "Catalog": the Admin section will grow
  // catalog-shaped surfaces for other domains (#222), and a bare "Catalog"
  // would have to be renamed the moment the second one lands. It is also what
  // the domain is called everywhere else in the migration — the item catalog.
  "item-catalog": "Item Catalog",
  // The source's label verbatim; the bar uppercases it, as MUI's tab did.
  "external-apis": "Integrations",
  "share-links": "Share Links",
};

/**
 * The tab a bare `/admin` opens. The old page resolved a missing *or*
 * unrecognized slug to its first tab rather than erroring, and the shell keeps
 * that: the route's schema drops what it does not know, and the page falls back
 * here.
 */
export const DEFAULT_ADMIN_TAB_SLUG: AdminTabSlug = ADMIN_TAB_SLUGS[0];

/**
 * The Admin section's admission rule: the flag AND the capability, together.
 *
 * One rule with two readers, because the section has two doors and they must
 * never disagree — the header decides whether to *draw* the entrance
 * (`AppHeader`'s `useNavTabs`, through the React facades) and the route guard
 * decides whether to *open* it (`requireAdminSectionAccess`, through the
 * isomorphic ones). Split across both call sites, the pair drifts into the two
 * bugs that matter: a door that 404s, or a section reachable by URL after the
 * flag was thrown to shut it.
 *
 * The old stack asked both questions in one condition
 * (`!enableAdminTab || !groupPermissions.isDigitalOpsAdmin`, timmy
 * `src/routes/admin-route.tsx`); this is that condition, named. Each caller
 * still performs its own reads — a pure predicate cannot call a hook, and
 * `lib/` must not reach up into `hooks/` (`client/docs/architecture.md`, "The
 * dependency rule").
 *
 * Advisory about the *capability*, as ever: Python use cases remain the
 * enforcement (#67 §4). The flag half is not advisory — it is the section's
 * kill switch.
 */
export function canEnterAdminSection({
  adminTabEnabled,
  canAdministrate,
}: {
  adminTabEnabled: boolean;
  canAdministrate: boolean;
}): boolean {
  return adminTabEnabled && canAdministrate;
}

/**
 * The Integrations tab's two directions, carried as `?mode=` — the source's
 * vocabulary (`ExternalApis.tsx:33,562-563`), Provided first as its toggle
 * draws them. Both values are named now so the Provided mode's ticket enables
 * an option rather than touching the search schema, which keeps `?mode=` as the
 * free string the source read.
 */
export const INTEGRATION_MODES = ["provided", "consumed"] as const;

export type IntegrationMode = (typeof INTEGRATION_MODES)[number];

/**
 * `?mode=` as the URL carried it: any text, or every value of a repeated param
 * (`@/lib/router-search-codec` keeps a link's own spelling).
 */
export type IntegrationModeParam = string | readonly string[] | undefined;

/**
 * The direction the tab shows for a `?mode=` value.
 *
 * **Every value resolves to Consumed while Provided is a Waiting affordance**
 * (owner ruling on #747, 2026-10-06): a bare `?tab=external-apis`, or an old
 * `?mode=provided` link, opens the working mode rather than one Atlas does not
 * have yet. The Provided ticket replaces this body with the source's rule —
 * anything other than `consumed` is `provided`, reading a repeated param as
 * its first value as `searchParams.get` did — and that is the one line it
 * flips.
 */
export function effectiveIntegrationMode(_mode: IntegrationModeParam): IntegrationMode {
  return "consumed";
}
